// Admin Firebase Functions
// Provides utility functions for admin dashboard operations

import { collection, getDocs, getDoc, doc, query, where, orderBy, limit, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './config';

// Max documents fetched per admin query. When a result set hits the cap, the
// UI shows a notice so admins know the list may be truncated.
export const ADMIN_LIST_LIMIT = 100;
export const ADMIN_SEARCH_LIMIT = 200;

/**
 * Get all tournaments with optional filtering by status
 * @param {string} filterStatus - 'all', 'public', 'flagged', or 'removed'
 * @returns {Promise<{tournaments: Array, error: string|null}>}
 */
export async function getAllTournamentsAdmin(filterStatus = 'all') {
  try {
    console.log('[Admin] Fetching tournaments with filter:', filterStatus);
    const tournamentsRef = collection(db, 'tournaments');
    let q;

    switch (filterStatus) {
      case 'public':
        q = query(tournamentsRef, where('isPublic', '==', true), orderBy('createdAt', 'desc'), limit(ADMIN_LIST_LIMIT));
        break;
      case 'flagged':
        q = query(tournamentsRef, where('isFlagged', '==', true), orderBy('createdAt', 'desc'), limit(ADMIN_LIST_LIMIT));
        break;
      case 'removed':
        q = query(tournamentsRef, where('isRemoved', '==', true), orderBy('createdAt', 'desc'), limit(ADMIN_LIST_LIMIT));
        break;
      default: // 'all'
        q = query(tournamentsRef, orderBy('createdAt', 'desc'), limit(ADMIN_LIST_LIMIT));
    }

    const snapshot = await getDocs(q);
    const tournaments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Enrich tournaments with user email/displayName
    const enrichedTournaments = await Promise.all(
      tournaments.map(async (tournament) => {
        if (tournament.createdBy) {
          try {
            const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', tournament.createdBy), limit(1)));
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              return {
                ...tournament,
                ownerEmail: userData.email || null,
                ownerDisplayName: userData.displayName || null
              };
            }
          } catch (err) {
            console.error('Error fetching user data:', err);
          }
        }
        return tournament;
      })
    );

    console.log('[Admin] Fetched', enrichedTournaments.length, 'tournaments');
    console.log('[Admin] Sample tournament:', enrichedTournaments[0]);

    return { tournaments: enrichedTournaments, error: null, truncated: tournaments.length >= ADMIN_LIST_LIMIT };
  } catch (error) {
    console.error('Error fetching tournaments:', error);
    return { tournaments: [], error: error.message };
  }
}

/**
 * Search tournaments by title, venue name, or user ID
 * @param {string} searchQuery - Search term
 * @param {string} filterStatus - Optional filter: 'all', 'public', 'flagged', or 'removed'
 * @returns {Promise<{tournaments: Array, error: string|null}>}
 */
export async function searchTournamentsAdmin(searchQuery, filterStatus = 'all') {
  try {
    const tournamentsRef = collection(db, 'tournaments');
    const snapshot = await getDocs(query(tournamentsRef, orderBy('createdAt', 'desc'), limit(ADMIN_SEARCH_LIMIT)));

    const searchLower = searchQuery.toLowerCase();

    // First, enrich all tournaments with user data
    const enrichedTournaments = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const tournament = { id: doc.id, ...doc.data() };

        if (tournament.createdBy) {
          try {
            const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', tournament.createdBy), limit(1)));
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              tournament.ownerEmail = userData.email || null;
              tournament.ownerDisplayName = userData.displayName || null;
            }
          } catch (err) {
            console.error('Error fetching user data:', err);
          }
        }

        return tournament;
      })
    );

    // Then filter by search and status
    const filteredTournaments = enrichedTournaments.filter(tournament => {
      // Apply search filter (now includes owner email and display name)
      const titleMatch = tournament.title?.toLowerCase().includes(searchLower);
      const venueMatch = tournament.venue?.name?.toLowerCase().includes(searchLower);
      const userIdMatch = tournament.createdBy?.toLowerCase().includes(searchLower);
      const emailMatch = tournament.ownerEmail?.toLowerCase().includes(searchLower);
      const nameMatch = tournament.ownerDisplayName?.toLowerCase().includes(searchLower);
      const matchesSearch = titleMatch || venueMatch || userIdMatch || emailMatch || nameMatch;

      // Apply status filter
      let matchesStatus = true;
      switch (filterStatus) {
        case 'public':
          matchesStatus = tournament.isPublic === true;
          break;
        case 'flagged':
          matchesStatus = tournament.isFlagged === true;
          break;
        case 'removed':
          matchesStatus = tournament.isRemoved === true;
          break;
        default: // 'all'
          matchesStatus = true;
      }

      return matchesSearch && matchesStatus;
    });

    // Truncation here reflects the raw fetch cap, not the filtered count: if we
    // pulled the max docs, a matching tournament could exist beyond the window.
    return { tournaments: filteredTournaments, error: null, truncated: snapshot.docs.length >= ADMIN_SEARCH_LIMIT };
  } catch (error) {
    console.error('Error searching tournaments:', error);
    return { tournaments: [], error: error.message };
  }
}

/**
 * Update tournament data (admin override)
 * Uses Cloud Function for server-side authorization
 * @param {string} tournamentId - Tournament ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export async function updateTournamentAdmin(tournamentId, updates) {
  try {
    const adminUpdateTournament = httpsCallable(functions, 'adminUpdateTournament');
    await adminUpdateTournament({ tournamentId, updates });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating tournament:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Permanently delete a tournament from the database
 * Uses Cloud Function for server-side authorization
 * @param {string} tournamentId - Tournament ID
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export async function deleteTournamentAdmin(tournamentId) {
  try {
    const adminDeleteTournament = httpsCallable(functions, 'adminDeleteTournament');
    await adminDeleteTournament({ tournamentId });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting tournament:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get all users with optional sorting
 * @param {string} sortBy - 'recent', 'tournaments', or 'alphabetical'
 * @returns {Promise<{users: Array, error: string|null}>}
 */
export async function getAllUsersAdmin(sortBy = 'recent') {
  try {
    const usersRef = collection(db, 'users');
    let q;

    switch (sortBy) {
      case 'tournaments':
        q = query(usersRef, orderBy('tournamentCount', 'desc'), limit(100));
        break;
      case 'alphabetical':
        q = query(usersRef, orderBy('email', 'asc'), limit(100));
        break;
      default: // 'recent'
        q = query(usersRef, orderBy('createdAt', 'desc'), limit(100));
    }

    const snapshot = await getDocs(q);

    // Get tournament counts for each user
    const usersWithCounts = await Promise.all(
      snapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        const tournamentsRef = collection(db, 'tournaments');
        const userTournamentsQuery = query(tournamentsRef, where('userId', '==', userDoc.id));
        const tournamentsSnapshot = await getDocs(userTournamentsQuery);

        return {
          uid: userDoc.id,
          ...userData,
          tournamentCount: tournamentsSnapshot.size
        };
      })
    );

    return { users: usersWithCounts, error: null };
  } catch (error) {
    console.error('Error fetching users:', error);
    return { users: [], error: error.message };
  }
}

/**
 * Search users by email or display name
 * @param {string} searchQuery - Search term
 * @returns {Promise<{users: Array, error: string|null}>}
 */
export async function searchUsersAdmin(searchQuery) {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(query(usersRef, limit(100)));

    const searchLower = searchQuery.toLowerCase();
    const users = snapshot.docs
      .map(doc => ({
        uid: doc.id,
        ...doc.data()
      }))
      .filter(user => {
        const emailMatch = user.email?.toLowerCase().includes(searchLower);
        const nameMatch = user.displayName?.toLowerCase().includes(searchLower);
        return emailMatch || nameMatch;
      });

    // Get tournament counts for matched users
    const usersWithCounts = await Promise.all(
      users.map(async (user) => {
        const tournamentsRef = collection(db, 'tournaments');
        const userTournamentsQuery = query(tournamentsRef, where('userId', '==', user.uid));
        const tournamentsSnapshot = await getDocs(userTournamentsQuery);

        return {
          ...user,
          tournamentCount: tournamentsSnapshot.size
        };
      })
    );

    return { users: usersWithCounts, error: null };
  } catch (error) {
    console.error('Error searching users:', error);
    return { users: [], error: error.message };
  }
}

/**
 * Get all tournaments for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<{tournaments: Array, error: string|null}>}
 */
export async function getUserTournamentsAdmin(userId) {
  try {
    const tournamentsRef = collection(db, 'tournaments');
    const q = query(
      tournamentsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const tournaments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { tournaments, error: null };
  } catch (error) {
    console.error('Error fetching user tournaments:', error);
    return { tournaments: [], error: error.message };
  }
}

/**
 * Get comprehensive site statistics
 * @returns {Promise<{statistics: Object, error: string|null}>}
 */
export async function getSiteStatistics() {
  try {
    // Get all collections data
    const [usersSnapshot, tournamentsSnapshot, reportsSnapshot] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'tournaments')),
      getDocs(collection(db, 'reports'))
    ]);

    // Calculate basic counts
    const totalUsers = usersSnapshot.size;
    const totalTournaments = tournamentsSnapshot.size;
    const totalReports = reportsSnapshot.size;

    // Calculate tournament statistics
    const tournaments = tournamentsSnapshot.docs.map(doc => doc.data());
    const publicTournaments = tournaments.filter(t => t.isPublic).length;
    const flaggedTournaments = tournaments.filter(t => t.isFlagged).length;

    // Calculate format breakdown
    const formatBreakdown = {};
    tournaments.forEach(t => {
      const format = t.format || 'Unknown';
      formatBreakdown[format] = (formatBreakdown[format] || 0) + 1;
    });

    // Get recent tournaments (last 10)
    const recentTournaments = tournaments
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      .slice(0, 10)
      .map(t => ({
        id: t.id,
        title: t.title,
        format: t.format,
        userEmail: t.userEmail,
        createdAt: t.createdAt
      }));

    // Calculate popular venues
    const venueCount = {};
    tournaments.forEach(t => {
      if (t.venue?.name) {
        const venueName = t.venue.name;
        if (!venueCount[venueName]) {
          venueCount[venueName] = {
            name: venueName,
            city: t.venue.city,
            state: t.venue.state,
            count: 0
          };
        }
        venueCount[venueName].count++;
      }
    });
    const popularVenues = Object.values(venueCount)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Calculate active users (users who logged in within last 30 days)
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const activeUsers30d = usersSnapshot.docs.filter(doc => {
      const lastLogin = doc.data().lastLoginAt?.seconds * 1000 || 0;
      return lastLogin > thirtyDaysAgo;
    }).length;

    // Calculate new users/tournaments this month
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const startOfMonthTimestamp = startOfMonth.getTime();

    const newUsersThisMonth = usersSnapshot.docs.filter(doc => {
      const created = doc.data().createdAt?.seconds * 1000 || 0;
      return created >= startOfMonthTimestamp;
    }).length;

    const newTournamentsThisMonth = tournaments.filter(t => {
      const created = t.createdAt?.seconds * 1000 || 0;
      return created >= startOfMonthTimestamp;
    }).length;

    // Calculate average tournaments per user
    const avgTournamentsPerUser = totalUsers > 0 ? totalTournaments / totalUsers : 0;

    const statistics = {
      totalUsers,
      totalTournaments,
      publicTournaments,
      flaggedTournaments,
      totalReports,
      activeUsers30d,
      formatBreakdown,
      recentTournaments,
      popularVenues,
      newUsersThisMonth,
      newTournamentsThisMonth,
      avgTournamentsPerUser
    };

    return { statistics, error: null };
  } catch (error) {
    console.error('Error fetching site statistics:', error);
    return { statistics: null, error: error.message };
  }
}

/**
 * Get all reports with tournament and user details
 * @returns {Promise<{reports: Array, error: string|null}>}
 */
export async function getAllReportsAdmin() {
  try {
    const reportsSnapshot = await getDocs(
      query(collection(db, 'reports'), orderBy('createdAt', 'desc'), limit(100))
    );

    const reports = await Promise.all(
      reportsSnapshot.docs.map(async (reportDoc) => {
        const reportData = { id: reportDoc.id, ...reportDoc.data() };

        // Get tournament details
        if (reportData.tournamentId) {
          try {
            const tournamentDoc = await getDoc(doc(db, 'tournaments', reportData.tournamentId));
            if (tournamentDoc.exists()) {
              const tournamentData = tournamentDoc.data();
              reportData.tournamentTitle = tournamentData.title || 'Unknown Tournament';
              reportData.tournamentVenue = tournamentData.venue?.name || null;
              reportData.tournamentIsFlagged = tournamentData.isFlagged || false;
              reportData.tournamentIsRemoved = tournamentData.isRemoved || false;
            } else {
              reportData.tournamentTitle = 'Deleted Tournament';
            }
          } catch (err) {
            console.error('Error fetching tournament for report:', err);
            reportData.tournamentTitle = 'Error loading';
          }
        }

        // Get reporter details
        if (reportData.reportedBy) {
          try {
            const userDoc = await getDoc(doc(db, 'users', reportData.reportedBy));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              reportData.reporterEmail = userData.email || null;
              reportData.reporterDisplayName = userData.displayName || null;
            }
          } catch (err) {
            console.error('Error fetching user for report:', err);
          }
        }

        return reportData;
      })
    );

    return { reports, error: null };
  } catch (error) {
    console.error('Error fetching all reports:', error);
    return { reports: [], error: error.message };
  }
}

/**
 * Get all flagged tournaments with their reports
 * @returns {Promise<{tournaments: Array, error: string|null}>}
 */
export async function getFlaggedTournamentsAdmin() {
  try {
    const tournamentsSnapshot = await getDocs(
      query(collection(db, 'tournaments'), where('isFlagged', '==', true), orderBy('flaggedAt', 'desc'), limit(50))
    );

    const tournaments = await Promise.all(
      tournamentsSnapshot.docs.map(async (tournamentDoc) => {
        const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };

        // Get owner details
        if (tournamentData.createdBy) {
          try {
            const userDoc = await getDoc(doc(db, 'users', tournamentData.createdBy));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              tournamentData.ownerEmail = userData.email || null;
              tournamentData.ownerDisplayName = userData.displayName || null;
            }
          } catch (err) {
            console.error('Error fetching owner for tournament:', err);
          }
        }

        // Get reports for this tournament
        try {
          const reportsSnapshot = await getDocs(
            query(collection(db, 'reports'), where('tournamentId', '==', tournamentDoc.id))
          );
          tournamentData.reports = reportsSnapshot.docs.map(r => ({ id: r.id, ...r.data() }));
        } catch (err) {
          console.error('Error fetching reports for tournament:', err);
          tournamentData.reports = [];
        }

        return tournamentData;
      })
    );

    return { tournaments, error: null };
  } catch (error) {
    console.error('Error fetching flagged tournaments:', error);
    return { tournaments: [], error: error.message };
  }
}

// ── Sync health (director sync-failure telemetry) ─────────────────────────────
// syncIncidents: written by the detectStalledSync scheduled function when a live
// occurrence goes quiet. syncDiagnostics: out-of-band beacons posted by director
// browsers when sync wedges / on tab-close with unsynced edits. Both are readable
// by admins only (see firestore.rules).

export async function getSyncIncidents() {
  try {
    const snap = await getDocs(query(collection(db, 'syncIncidents'), orderBy('detectedAt', 'desc'), limit(100)));
    return { incidents: snap.docs.map((d) => ({ id: d.id, ...d.data() })), error: null };
  } catch (error) {
    console.error('[Admin] getSyncIncidents error:', error);
    return { incidents: [], error: error.message };
  }
}

export async function getSyncDiagnostics() {
  try {
    const snap = await getDocs(query(collection(db, 'syncDiagnostics'), orderBy('receivedAt', 'desc'), limit(100)));
    return { reports: snap.docs.map((d) => ({ id: d.id, ...d.data() })), error: null };
  } catch (error) {
    console.error('[Admin] getSyncDiagnostics error:', error);
    return { reports: [], error: error.message };
  }
}

export async function resolveSyncIncident(id) {
  try {
    await updateDoc(doc(db, 'syncIncidents', id), { resolved: true, resolvedAt: serverTimestamp() });
    return { error: null };
  } catch (error) {
    console.error('[Admin] resolveSyncIncident error:', error);
    return { error: error.message };
  }
}
